import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const Arrows = styled.div`
  display: none; gap: 8px;
  @media (min-width: 1024px) { display: inline-flex; }
  button {
    width: 36px; height: 36px; border: 1px solid currentColor; border-radius: 50%;
    color: inherit; background: transparent; font: inherit; font-size: 22px; cursor: pointer;
  }
  button:disabled { visibility: hidden; pointer-events: none; }
`;

/**
 * Wraps a row's scrolling track so `edgeArrows` can overlay its edges without
 * disturbing the track's own edge-to-edge bleed — the track keeps its
 * negative margins; the arrows position against this box's padded content
 * width, which (via HUB_TRACK_GUTTER_CSS cancelling that same bleed) lines up
 * with the first/last card's edge. Same split as the PDP "More like this"
 * rail's RailViewport/RailArrow.
 */
export const RowViewport = styled.div`
  position: relative;
`;

// Desktop-only edge arrow, vertically centered on the row (top: 50% of
// RowViewport, which is the track's own height — every card in a row is
// stretched to match the tallest one, so this centers on the cards
// regardless of which row it is). Rendered only when that direction still has
// something to scroll to, so it reads as the Amazon / Google-Shopping
// "there's more this way" affordance rather than a decoration. Phone never
// sees it — a mouse can't swipe, but a finger can.
//
// Kate (2026-10-07): hidden until the row is hovered or has focus within it
// (Spotify/Netflix rail arrows), rather than sitting on the row at rest.
// Opacity/pointer-events (not display) carry the reveal so the arrow stays
// in the a11y tree and can itself receive focus — by the time Tab reaches
// the button, `:focus-within` on RowViewport is already true because the
// button is the thing that has focus.
const EdgeArrow = styled.button<{ $dir: 'prev' | 'next' }>`
  display: none;
  @media (min-width: 1024px) { display: flex; }
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => ($dir === 'prev' ? 'left: 4px;' : 'right: 4px;')}
  z-index: 2;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: hsla(0, 0%, 100%, 0.94);
  color: ${({ theme }) => theme.color.inkSoft};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 8px hsla(28, 40%, 40%, 0.2);
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--dur-press) ease;
  &:hover { background: hsl(0, 0%, 100%); }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.clay}; outline-offset: 2px; }
  ${RowViewport}:hover &,
  ${RowViewport}:focus-within & {
    opacity: 1;
    pointer-events: auto;
  }
`;

/** One user action, one scroll. Explicit instant avoids the root's CSS smooth
 * behavior, which broke earlier measure/scroll loops. Native touch still works. */
export function useHubRowScroll(label: string, itemCount: number, leadingItemKey?: string) {
  const ref = useRef<HTMLDivElement>(null);
  const atStart = useRef(true);
  const previousLeadingItem = useRef(leadingItemKey);
  const [edges, setEdges] = useState({ start: true, end: true });
  const measure = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    const start = node.scrollLeft <= 1;
    const end = node.scrollLeft + node.clientWidth >= node.scrollWidth - 1;
    atStart.current = start;
    setEdges(old => old.start === start && old.end === end ? old : { start, end });
  }, []);
  useBeforePaint(() => {
    // CSS snap preserves the old leading card when hydration prepends personal
    // picks. Keep a row that was at its start at the new start, before paint;
    // leave a deliberately scrolled row on the card the visitor was browsing.
    if (previousLeadingItem.current !== leadingItemKey && atStart.current && ref.current) {
      ref.current.scrollLeft = 0;
    }
    previousLeadingItem.current = leadingItemKey;
    measure();
  }, [leadingItemKey, measure]);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(node);
    Array.from(node.children).forEach(child => observer?.observe(child));
    window.addEventListener('resize', measure);
    return () => { node.removeEventListener('scroll', measure); observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [itemCount, measure]);
  const move = (direction: number) => {
    const node = ref.current;
    if (!node) return;
    node.scrollBy({ left: direction * node.clientWidth * 0.8, behavior: 'instant' as ScrollBehavior });
    measure();
  };
  const controls = <Arrows aria-label={`${label} scrolling`}>
    <button type="button" aria-label={`Previous ${label}`} disabled={edges.start} onClick={() => move(-1)}>‹</button>
    <button type="button" aria-label={`Next ${label}`} disabled={edges.end} onClick={() => move(1)}>›</button>
  </Arrows>;
  const edgeArrows = <>
    {!edges.start && (
      <EdgeArrow type="button" $dir="prev" aria-label={`Previous ${label}`} onClick={() => move(-1)}>‹</EdgeArrow>
    )}
    {!edges.end && (
      <EdgeArrow type="button" $dir="next" aria-label={`Next ${label}`} onClick={() => move(1)}>›</EdgeArrow>
    )}
  </>;
  return { ref, controls, edgeArrows };
}
